import { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

interface EmptyStateProps {
  icon: LucideIcon;
  title: string;
  description?: string;
  size?: 'default' | 'compact';
  /**
   * Optional action rendered below the description. Use this to surface a
   * CTA such as "Clear filters" alongside the empty-state copy without
   * forking the component.
   */
  action?: ReactNode;
}

const SIZE_CLASSES = {
  default: {
    container: 'flex flex-col items-center justify-center h-64 space-y-4',
    icon: 'w-16 h-16 text-muted-foreground',
    title: 'text-xl font-semibold text-foreground',
    description: 'text-muted-foreground',
    action: 'pt-2',
  },
  compact: {
    container: 'flex flex-col items-center justify-center gap-1.5 py-8 text-center',
    icon: 'h-6 w-6 text-muted-foreground',
    title: 'text-sm font-medium text-foreground',
    description: 'max-w-sm text-xs text-muted-foreground',
    action: 'pt-2',
  },
} as const;

export function EmptyState({ icon: Icon, title, description, size = 'default', action }: EmptyStateProps) {
  const styles = SIZE_CLASSES[size];
  return (
    <div className={styles.container}>
      <Icon className={styles.icon} />
      <h2 className={styles.title}>{title}</h2>
      {description && <p className={styles.description}>{description}</p>}
      {action && <div className={styles.action}>{action}</div>}
    </div>
  );
}
